import { useState, useRef } from 'react';
import Layout from '../../../../components/layout/Layout';

interface Campanha {
  id: string;
  nome: string;
  status: 'Ativa' | 'Planejada';
  tipo: string;
  participantes: number;
}

interface Participante {
  id: string;
  nome: string;
  email: string;
  documento: string;
  cargo: string;
  status: 'Pendente' | 'Ativo' | 'Inativo' | 'Suspenso';
}

export default function EnviarConvitesPage() {
  const [campanhasSelecionadas, setCampanhasSelecionadas] = useState<string[]>([]);
  const [participantesSelecionados, setParticipantesSelecionados] = useState<string[]>([]);
  const [filtroDestinatarios, setFiltroDestinatarios] = useState<'todos' | 'selecionados'>('todos');
  const [buscaParticipante, setBuscaParticipante] = useState('');
  const [canaisSelecionados, setCanaisSelecionados] = useState<string[]>(['email']);
  const [assuntoEmail, setAssuntoEmail] = useState('Convite para Programa de Incentivo');
  const [mensagemEmail, setMensagemEmail] = useState('Olá {nome},\n\nVocê foi convidado(a) para participar do nosso programa de incentivo!\n\nClique no link abaixo para aceitar o convite e começar a participar:\n{link_convite}\n\nAtenciosamente,\nEquipe de Incentivos');
  const [mensagemSMS, setMensagemSMS] = useState('Olá {nome}! Você foi convidado para o programa de incentivo. Acesse: {link_convite}');
  const [mensagemWhatsApp, setMensagemWhatsApp] = useState('Olá {nome}! 👋\n\nVocê foi convidado(a) para participar do nosso programa de incentivo! 🎉\n\nClique no link para aceitar:\n{link_convite}\n\nQualquer dúvida, estamos à disposição!');
  const [showImageUpload, setShowImageUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const campanhas: Campanha[] = [
    { id: '1', nome: 'Vendas Q4 2024', status: 'Ativa', tipo: 'Vendas', participantes: 8 },
    { id: '2', nome: 'Meta Dezembro', status: 'Ativa', tipo: 'Performance', participantes: 7 },
    { id: '3', nome: 'Captação Novos Clientes', status: 'Ativa', tipo: 'Aquisição', participantes: 5 },
    { id: '4', nome: 'Capacitação Equipe', status: 'Planejada', tipo: 'Capacitação', participantes: 0 },
    { id: '5', nome: 'Retenção de Clientes', status: 'Ativa', tipo: 'Retenção', participantes: 6 }
  ];

  const participantes: Participante[] = [
    { id: '1', nome: 'João Silva Santos', email: 'joao.silva@empresa.com', documento: '123.456.789-00', cargo: 'Consultor de Vendas', status: 'Pendente' },
    { id: '2', nome: 'Maria Oliveira Costa', email: 'maria.oliveira@empresa.com', documento: '987.654.321-00', cargo: 'Analista de Marketing', status: 'Pendente' },
    { id: '3', nome: 'Pedro Rodrigues Lima', email: 'pedro.rodrigues@empresa.com', documento: '456.789.123-00', cargo: 'Desenvolvedor', status: 'Pendente' },
    { id: '4', nome: 'Ana Carolina Souza', email: 'ana.souza@empresa.com', documento: '789.123.456-00', cargo: 'Analista de RH', status: 'Pendente' },
    { id: '5', nome: 'Carlos Eduardo Ferreira', email: 'carlos.ferreira@empresa.com', documento: '321.654.987-00', cargo: 'Analista Financeiro', status: 'Pendente' },
    { id: '6', nome: 'Juliana Santos Almeida', email: 'juliana.almeida@empresa.com', documento: '147.258.369-00', cargo: 'Gerente de Vendas', status: 'Pendente' },
    { id: '7', nome: 'Ricardo Mendes Oliveira', email: 'ricardo.mendes@empresa.com', documento: '258.369.147-00', cargo: 'Supervisor', status: 'Pendente' },
    { id: '8', nome: 'Fernanda Costa Lima', email: 'fernanda.lima@empresa.com', documento: '369.147.258-00', cargo: 'Coordenadora', status: 'Pendente' }
  ];

  const handleToggleCampanha = (id: string) => {
    setCampanhasSelecionadas(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleToggleParticipante = (id: string) => {
    setParticipantesSelecionados(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleToggleCanal = (canal: string) => {
    setCanaisSelecionados(prev => {
      if (prev.includes(canal)) {
        // Não permitir desmarcar se for o único canal selecionado
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== canal);
      } else {
        return [...prev, canal];
      }
    });
  };

  const handleSelecionarTodasCampanhas = () => {
    if (campanhasSelecionadas.length === campanhas.length) {
      setCampanhasSelecionadas([]);
    } else {
      setCampanhasSelecionadas(campanhas.map(c => c.id));
    }
  };

  const handleSelecionarTodosParticipantes = () => {
    const participantesFiltrados = getParticipantesFiltrados();
    if (participantesSelecionados.length === participantesFiltrados.length) {
      setParticipantesSelecionados([]);
    } else {
      setParticipantesSelecionados(participantesFiltrados.map(p => p.id));
    }
  };

  const getParticipantesFiltrados = () => {
    return participantes.filter(p => {
      const matchBusca = p.nome.toLowerCase().includes(buscaParticipante.toLowerCase()) ||
                         p.email.toLowerCase().includes(buscaParticipante.toLowerCase()) ||
                         p.documento.includes(buscaParticipante);
      return matchBusca;
    });
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'Pendente': 'bg-orange-100 text-orange-800',
      'Ativo': 'bg-green-100 text-green-800',
      'Inativo': 'bg-gray-100 text-gray-800',
      'Suspenso': 'bg-red-100 text-red-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      'Pendente': 'ri-time-line',
      'Ativo': 'ri-check-line',
      'Inativo': 'ri-close-circle-line',
      'Suspenso': 'ri-error-warning-line'
    };
    return icons[status as keyof typeof icons] || 'ri-question-line';
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        const imageTag = `<img src="${imageUrl}" alt="Imagem do convite" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
        setMensagemEmail(prev => prev + '\n\n' + imageTag);
      };
      reader.readAsDataURL(file);
    }
  };

  const applyFormatting = (command: string, value?: string) => {
    document.execCommand(command, false, value);
  };

  const insertVariable = (variable: string) => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(variable));
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const participantesFiltrados = getParticipantesFiltrados();

  // Calcular estatísticas dos canais
  const totalConvites = filtroDestinatarios === 'todos' ? participantes.length : participantesSelecionados.length;
  const convitesPorEmail = canaisSelecionados.includes('email') ? totalConvites : 0;
  const convitesPorSMS = canaisSelecionados.includes('sms') ? totalConvites : 0;
  const convitesPorWhatsApp = canaisSelecionados.includes('whatsapp') ? totalConvites : 0;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Enviar Convites</h1>
            <p className="text-gray-600 mt-1">Convide participantes para o programa de incentivo</p>
          </div>
          <button
            onClick={() => window.history.back()}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            <span>Voltar</span>
          </button>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total de Convites</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">{totalConvites}</p>
              </div>
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-emerald-50/50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">Por Email</p>
                <p className="text-3xl font-bold text-emerald-900 mt-1">{convitesPorEmail}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-mail-send-line text-white text-xl"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-purple-50/50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Por SMS</p>
                <p className="text-3xl font-bold text-purple-900 mt-1">{convitesPorSMS}</p>
              </div>
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-message-3-line text-white text-xl"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-orange-50/50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Por WhatsApp</p>
                <p className="text-3xl font-bold text-orange-900 mt-1">{convitesPorWhatsApp}</p>
              </div>
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-white text-xl"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Campanhas */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Campanhas</h3>
              <p className="text-sm text-gray-600 mt-1">Selecione múltiplas campanhas para as quais os participantes serão convidados</p>
            </div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={campanhasSelecionadas.length === campanhas.length}
                onChange={handleSelecionarTodasCampanhas}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Selecionar todas as campanhas</span>
            </label>
          </div>

          {campanhasSelecionadas.length > 0 && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-800">
                  {campanhasSelecionadas.length} {campanhasSelecionadas.length === 1 ? 'campanha selecionada' : 'campanhas selecionadas'}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {campanhasSelecionadas.map(id => {
                  const campanha = campanhas.find(c => c.id === id);
                  return campanha ? (
                    <span key={id} className="inline-flex items-center px-3 py-1 bg-white border border-green-300 rounded-full text-sm text-gray-700">
                      {campanha.nome}
                      <button
                        onClick={() => handleToggleCampanha(id)}
                        className="ml-2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-close-line"></i>
                        </div>
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}

          {campanhasSelecionadas.length === 0 && (
            <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-orange-600"></i>
                </div>
                <p className="text-sm text-orange-800">Nenhuma campanha selecionada. Os participantes receberão apenas o convite geral para o programa.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {campanhas.map(campanha => (
              <div
                key={campanha.id}
                onClick={() => handleToggleCampanha(campanha.id)}
                className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  campanhasSelecionadas.includes(campanha.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <input
                        type="checkbox"
                        checked={campanhasSelecionadas.includes(campanha.id)}
                        onChange={() => {}}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <h4 className="font-semibold text-gray-900">{campanha.nome}</h4>
                    </div>
                    <div className="flex items-center space-x-3 ml-6">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        campanha.status === 'Ativa' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {campanha.status}
                      </span>
                      <span className="text-xs text-gray-600">{campanha.tipo}</span>
                      <span className="text-xs text-gray-600 flex items-center space-x-1">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-user-line"></i>
                        </div>
                        <span>{campanha.participantes} participantes</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Destinatários */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Destinatários</h3>
            <p className="text-sm text-gray-600 mt-1">Selecione os participantes que receberão os convites</p>
          </div>

          {/* Filtros */}
          <div className="mb-4 space-y-4">
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="filtroDestinatarios"
                  checked={filtroDestinatarios === 'todos'}
                  onChange={() => setFiltroDestinatarios('todos')}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Todos os Participantes</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="filtroDestinatarios"
                  checked={filtroDestinatarios === 'selecionados'}
                  onChange={() => setFiltroDestinatarios('selecionados')}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Participantes Selecionados</span>
              </label>
            </div>

            {filtroDestinatarios === 'selecionados' && (
              <div className="space-y-3">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-search-line text-gray-400"></i>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={buscaParticipante}
                    onChange={(e) => setBuscaParticipante(e.target.value)}
                    placeholder="Buscar por nome, email, CPF ou CNPJ..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                {participantesSelecionados.length > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-800">
                        {participantesSelecionados.length} {participantesSelecionados.length === 1 ? 'participante selecionado' : 'participantes selecionados'}
                      </span>
                      <button
                        onClick={() => setParticipantesSelecionados([])}
                        className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer whitespace-nowrap"
                      >
                        Limpar seleção
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tabela de Participantes */}
          {filtroDestinatarios === 'selecionados' && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={participantesSelecionados.length === participantesFiltrados.length && participantesFiltrados.length > 0}
                    onChange={handleSelecionarTodosParticipantes}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Selecionar todos</span>
                </label>
                <span className="text-sm text-gray-600">
                  {participantesFiltrados.length} {participantesFiltrados.length === 1 ? 'participante encontrado' : 'participantes encontrados'}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documento</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {participantesFiltrados.map(participante => (
                      <tr key={participante.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={participantesSelecionados.includes(participante.id)}
                            onChange={() => handleToggleParticipante(participante.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{participante.nome}</div>
                          <div className="text-sm text-gray-500">{participante.cargo}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{participante.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{participante.documento}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(participante.status)}`}>
                            <div className="w-3 h-3 flex items-center justify-center mr-1">
                              <i className={getStatusIcon(participante.status)}></i>
                            </div>
                            {participante.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {participantesFiltrados.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-user-search-line text-gray-400 text-2xl"></i>
                    </div>
                  </div>
                  <p className="text-gray-600">Nenhum participante encontrado com os critérios de busca</p>
                </div>
              )}
            </div>
          )}

          {filtroDestinatarios === 'todos' && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-blue-600"></i>
                </div>
                <p className="text-sm text-blue-800">
                  Todos os participantes com status "Pendente" receberão os convites para as campanhas selecionadas.
                  Total: <strong>{participantes.length} participantes</strong>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Canais de Envio */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Canais de Envio</h3>
            <p className="text-sm text-gray-600 mt-1">Selecione múltiplos canais para enviar os convites simultaneamente</p>
          </div>

          {/* Seletor de Canais Múltiplos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div
              onClick={() => handleToggleCanal('email')}
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                canaisSelecionados.includes('email')
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={canaisSelecionados.includes('email')}
                  onChange={() => {}}
                  className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                />
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  canaisSelecionados.includes('email') ? 'bg-emerald-500' : 'bg-gray-200'
                }`}>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`ri-mail-send-line text-xl ${
                      canaisSelecionados.includes('email') ? 'text-white' : 'text-gray-600'
                    }`}></i>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Email</h4>
                  <p className="text-sm text-gray-600">Envio por e-mail</p>
                </div>
              </div>
            </div>

            <div
              onClick={() => handleToggleCanal('sms')}
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                canaisSelecionados.includes('sms')
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={canaisSelecionados.includes('sms')}
                  onChange={() => {}}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  canaisSelecionados.includes('sms') ? 'bg-purple-500' : 'bg-gray-200'
                }`}>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`ri-message-3-line text-xl ${
                      canaisSelecionados.includes('sms') ? 'text-white' : 'text-gray-600'
                    }`}></i>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">SMS</h4>
                  <p className="text-sm text-gray-600">Envio por SMS</p>
                </div>
              </div>
            </div>

            <div
              onClick={() => handleToggleCanal('whatsapp')}
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                canaisSelecionados.includes('whatsapp')
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={canaisSelecionados.includes('whatsapp')}
                  onChange={() => {}}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  canaisSelecionados.includes('whatsapp') ? 'bg-orange-500' : 'bg-gray-200'
                }`}>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`ri-whatsapp-line text-xl ${
                      canaisSelecionados.includes('whatsapp') ? 'text-white' : 'text-gray-600'
                    }`}></i>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">WhatsApp</h4>
                  <p className="text-sm text-gray-600">Envio por WhatsApp</p>
                </div>
              </div>
            </div>
          </div>

          {canaisSelecionados.length > 1 && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-blue-600"></i>
                </div>
                <p className="text-sm text-blue-800">
                  Os convites serão enviados simultaneamente por {canaisSelecionados.length} canais: {canaisSelecionados.map(canal => 
                    canal === 'email' ? 'Email' : canal === 'sms' ? 'SMS' : 'WhatsApp'
                  ).join(', ')}.
                </p>
              </div>
            </div>
          )}

          {/* Formulários por Canal */}
          <div className="space-y-6">
            {/* Formulário de Email */}
            {canaisSelecionados.includes('email') && (
              <div className="border border-emerald-200 rounded-lg p-6 bg-emerald-50/30">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-6 h-6 bg-emerald-500 rounded flex items-center justify-center">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-send-line text-white"></i>
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Configuração do Email</h4>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assunto do Email
                    </label>
                    <input
                      type="text"
                      value={assuntoEmail}
                      onChange={(e) => setAssuntoEmail(e.target.value)}
                      placeholder="Digite o assunto do email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mensagem do Email
                    </label>
                    
                    {/* Barra de Ferramentas do Editor */}
                    <div className="border border-gray-300 rounded-t-lg bg-gray-50 p-2 flex items-center space-x-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => applyFormatting('bold')}
                        className="p-2 hover:bg-gray-200 rounded text-gray-600 hover:text-gray-800 transition-colors"
                        title="Negrito"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-bold"></i>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('italic')}
                        className="p-2 hover:bg-gray-200 rounded text-gray-600 hover:text-gray-800 transition-colors"
                        title="Itálico"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-italic"></i>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('underline')}
                        className="p-2 hover:bg-gray-200 rounded text-gray-600 hover:text-gray-800 transition-colors"
                        title="Sublinhado"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-underline"></i>
                        </div>
                      </button>
                      <div className="w-px h-6 bg-gray-300"></div>
                      <button
                        type="button"
                        onClick={() => applyFormatting('insertUnorderedList')}
                        className="p-2 hover:bg-gray-200 rounded text-gray-600 hover:text-gray-800 transition-colors"
                        title="Lista"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-list-unordered"></i>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('insertOrderedList')}
                        className="p-2 hover:bg-gray-200 rounded text-gray-600 hover:text-gray-800 transition-colors"
                        title="Lista Numerada"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-list-ordered"></i>
                        </div>
                      </button>
                      <div className="w-px h-6 bg-gray-300"></div>
                      <button
                        type="button"
                        onClick={() => setShowImageUpload(!showImageUpload)}
                        className="p-2 hover:bg-gray-200 rounded text-gray-600 hover:text-gray-800 transition-colors"
                        title="Inserir Imagem"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-image-line"></i>
                        </div>
                      </button>
                      <div className="w-px h-6 bg-gray-300"></div>
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-600">Variáveis:</span>
                        <button
                          type="button"
                          onClick={() => insertVariable('{nome}')}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
                        >
                          {'{nome}'}
                        </button>
                        <button
                          type="button"
                          onClick={() => insertVariable('{email}')}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
                        >
                          {'{email}'}
                        </button>
                        <button
                          type="button"
                          onClick={() => insertVariable('{link_convite}')}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
                        >
                          {'{link_convite}'}
                        </button>
                      </div>
                    </div>

                    {/* Upload de Imagem */}
                    {showImageUpload && (
                      <div className="border-l border-r border-gray-300 bg-gray-50 p-4">
                        <div className="flex items-center space-x-4">
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-upload-line"></i>
                            </div>
                            <span>Selecionar Imagem</span>
                          </button>
                          <span className="text-sm text-gray-600">Formatos aceitos: JPG, PNG, GIF (máx. 2MB)</span>
                        </div>
                      </div>
                    )}

                    {/* Editor de Texto Rico */}
                    <div
                      contentEditable
                      suppressContentEditableWarning={true}
                      className="w-full min-h-[200px] px-4 py-3 border-l border-r border-b border-gray-300 rounded-b-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm bg-white"
                      style={{ outline: 'none' }}
                      dangerouslySetInnerHTML={{ __html: mensagemEmail.replace(/\n/g, '<br>') }}
                      onBlur={(e) => setMensagemEmail(e.currentTarget.innerHTML.replace(/<br>/g, '\n').replace(/<[^>]*>/g, ''))}
                    />
                  </div>

                  <div className="flex items-center space-x-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-emerald-600"></i>
                    </div>
                    <p className="text-sm text-emerald-800">
                      O email será enviado com formatação HTML, incluirá o logo da empresa e suportará imagens incorporadas
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Formulário de SMS */}
            {canaisSelecionados.includes('sms') && (
              <div className="border border-purple-200 rounded-lg p-6 bg-purple-50/30">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-6 h-6 bg-purple-500 rounded flex items-center justify-center">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-message-3-line text-white"></i>
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Configuração do SMS</h4>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mensagem SMS
                    </label>
                    <textarea
                      value={mensagemSMS}
                      onChange={(e) => {
                        if (e.target.value.length <= 160) {
                          setMensagemSMS(e.target.value);
                        }
                      }}
                      rows={4}
                      placeholder="Digite a mensagem do SMS (máximo 160 caracteres)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-500">
                        Variáveis disponíveis: {'{nome}'}, {'{link_convite}'}
                      </p>
                      <p className={`text-xs font-medium ${
                        mensagemSMS.length > 160 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {mensagemSMS.length}/160 caracteres
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-purple-600"></i>
                    </div>
                    <p className="text-sm text-purple-800">
                      SMS limitado a 160 caracteres. Links serão encurtados automaticamente
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Formulário de WhatsApp */}
            {canaisSelecionados.includes('whatsapp') && (
              <div className="border border-orange-200 rounded-lg p-6 bg-orange-50/30">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-6 h-6 bg-orange-500 rounded flex items-center justify-center">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-whatsapp-line text-white"></i>
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Configuração do WhatsApp</h4>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mensagem WhatsApp
                    </label>
                    <textarea
                      value={mensagemWhatsApp}
                      onChange={(e) => setMensagemWhatsApp(e.target.value)}
                      rows={8}
                      placeholder="Digite a mensagem do WhatsApp"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Variáveis disponíveis: {'{nome}'}, {'{email}'}, {'{link_convite}'}
                    </p>
                  </div>

                  <div className="flex items-center space-x-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-orange-600"></i>
                    </div>
                    <p className="text-sm text-orange-800">
                      Mensagem será enviada via WhatsApp Business API. Emojis são suportados 😊
                    </p>
                  </div>

                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Pré-visualização</h4>
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-building-line text-white"></i>
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">
                            {mensagemWhatsApp.replace('{nome}', 'João Silva').replace('{link_convite}', 'https://app.exemplo.com/convite/abc123')}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">Agora</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modelos Salvos */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Modelos Salvos</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 transition-colors cursor-pointer">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-gray-900">Convite Padrão</h4>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-yellow-400"></i>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">Template padrão de convite para novos participantes</p>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Última edição: 15/12/2024</span>
              </div>
            </div>

            <div className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 transition-colors cursor-pointer">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-gray-900">Convite Vendas</h4>
              </div>
              <p className="text-sm text-gray-600 mb-3">Template específico para equipe de vendas</p>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Última edição: 10/12/2024</span>
              </div>
            </div>

            <div className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 transition-colors cursor-pointer">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-gray-900">Convite Gerencial</h4>
              </div>
              <p className="text-sm text-gray-600 mb-3">Template para gestores e líderes</p>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Última edição: 05/12/2024</span>
              </div>
            </div>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="flex items-center justify-end space-x-3 pb-6">
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              const totalDestinatarios = filtroDestinatarios === 'todos' 
                ? participantes.length 
                : participantesSelecionados.length;
              
              if (totalDestinatarios === 0) {
                alert('Selecione pelo menos um participante para enviar os convites.');
                return;
              }

              if (canaisSelecionados.length === 0) {
                alert('Selecione pelo menos um canal de envio.');
                return;
              }

              const canaisNomes = canaisSelecionados.map(canal => 
                canal === 'email' ? 'Email' : canal === 'sms' ? 'SMS' : 'WhatsApp'
              ).join(', ');
              
              alert(`Convites enviados com sucesso via ${canaisNomes} para ${totalDestinatarios} participante(s)!`);
            }}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-send-plane-fill"></i>
            </div>
            <span>Enviar Convites</span>
          </button>
        </div>
      </div>
    </Layout>
  );
}
